import { API_PATHS } from "@antares/shared";
import type {
  PodInfo,
  LoginResponse,
  MetricsSnapshot,
  FileEntry,
  FileContent,
} from "@antares/shared";

async function request<T>(
  path: string,
  options?: RequestInit
): Promise<T> {
  const headers: Record<string, string> = {};
  if (options?.body) {
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(path, {
    credentials: "include",
    headers,
    ...options,
  });

  if (res.status === 401) {
    // Session expired (e.g. pod restarted) — redirect to login but keep saved token
    window.location.hash = "#/login";
    throw new Error("Unauthorized");
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(body.message ?? `HTTP ${res.status}`);
  }

  if (res.status === 204) return undefined as T;
  return res.json();
}

export const api = {
  async checkAuth(): Promise<LoginResponse> {
    const res = await fetch(API_PATHS.AUTH_CHECK, { credentials: "include" });
    if (!res.ok) return { authenticated: false };
    return res.json();
  },

  loginWithToken(token: string): Promise<LoginResponse> {
    return request(API_PATHS.AUTH_LOGIN, {
      method: "POST",
      body: JSON.stringify({ token }),
    });
  },

  loginWithPassword(username: string, password: string): Promise<LoginResponse> {
    return request(API_PATHS.AUTH_LOGIN, {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
  },

  logout(): Promise<void> {
    return request(API_PATHS.AUTH_LOGOUT, { method: "DELETE" });
  },

  listPods(): Promise<PodInfo[]> {
    return request(API_PATHS.PODS);
  },

  getPod(ns: string, name: string): Promise<PodInfo> {
    return request(API_PATHS.POD(ns, name));
  },

  getLogHistory(ns: string, name: string, tail = 1000, container?: string): Promise<{ lines: string }> {
    const params = new URLSearchParams({ tail: String(tail) });
    if (container) params.set("container", container);
    return request(`${API_PATHS.POD_LOGS_HISTORY(ns, name)}?${params}`);
  },

  deletePod(ns: string, name: string): Promise<void> {
    return request(API_PATHS.POD(ns, name), { method: "DELETE" });
  },

  scalePod(
    ns: string,
    name: string,
    replicas: number
  ): Promise<{ replicas: number }> {
    return request(API_PATHS.POD_SCALE(ns, name), {
      method: "PATCH",
      body: JSON.stringify({ replicas }),
    });
  },

  listDirectory(
    ns: string,
    name: string,
    path: string,
    container?: string
  ): Promise<{ type: "directory"; path: string; entries: FileEntry[]; disk?: { totalBytes: number; usedBytes: number; freeBytes: number } }> {
    const params = new URLSearchParams({ path });
    if (container) params.set("container", container);
    return request(`${API_PATHS.POD_FS(ns, name)}?${params}`);
  },

  readFile(
    ns: string,
    name: string,
    path: string,
    container?: string
  ): Promise<{ type: "file"; path: string; size: number; tooLarge: boolean; content?: string; encoding?: string }> {
    const params = new URLSearchParams({ path });
    if (container) params.set("container", container);
    return request(`${API_PATHS.POD_FS(ns, name)}?${params}`);
  },

  writeFile(
    ns: string,
    name: string,
    path: string,
    content: string,
    container?: string
  ): Promise<{ ok: boolean }> {
    return request(API_PATHS.POD_FS(ns, name), {
      method: "PUT",
      body: JSON.stringify({ path, content, container }),
    });
  },

  moveFile(
    ns: string,
    name: string,
    from: string,
    to: string,
    container?: string
  ): Promise<{ ok: boolean }> {
    return request(API_PATHS.POD_FS_MOVE(ns, name), {
      method: "POST",
      body: JSON.stringify({ from, to, container }),
    });
  },

  downloadUrl(ns: string, name: string, path: string, container?: string): string {
    const params = new URLSearchParams({ path });
    if (container) params.set("container", container);
    return `${API_PATHS.POD_FS_DOWNLOAD(ns, name)}?${params}`;
  },

  async uploadFile(
    ns: string,
    name: string,
    path: string,
    file: File,
    container?: string
  ): Promise<{ ok: boolean }> {
    const params = new URLSearchParams({ path });
    if (container) params.set("container", container);
    const res = await fetch(`${API_PATHS.POD_FS_UPLOAD(ns, name)}?${params}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/octet-stream" },
      body: file,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(body.message ?? `Upload failed: ${res.status}`);
    }
    return res.json();
  },

  getMetrics(ns: string, name: string): Promise<MetricsSnapshot> {
    return request(API_PATHS.POD_METRICS(ns, name));
  },

  // Admin
  listUsers(): Promise<{ users: { username: string; createdAt: string }[] }> {
    return request(API_PATHS.ADMIN_USERS);
  },

  addUser(username: string, password: string): Promise<{ ok: boolean }> {
    return request(API_PATHS.ADMIN_USERS, {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
  },

  resetUserPassword(username: string, password: string): Promise<{ ok: boolean }> {
    return request(`${API_PATHS.ADMIN_USERS}/${encodeURIComponent(username)}`, {
      method: "PUT",
      body: JSON.stringify({ password }),
    });
  },

  removeUser(username: string): Promise<void> {
    return request(`${API_PATHS.ADMIN_USERS}/${encodeURIComponent(username)}`, {
      method: "DELETE",
    });
  },

  getSharedTokenStatus(): Promise<{ configured: boolean }> {
    return request("/api/admin/shared-token/status");
  },

  setSharedToken(token: string): Promise<{ ok: boolean }> {
    return request("/api/admin/shared-token", {
      method: "PUT",
      body: JSON.stringify({ token }),
    });
  },
};
